import React, { useState, useEffect, useRef } from 'react'
import { Button, Form, Row, Col, Container, Card } from 'react-bootstrap';
import { checkUserPermissions } from '../../helpers/permission';
import { BiImageAdd } from 'react-icons/bi';

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useNavigate, useLocation } from 'react-router-dom';
import * as yup from 'yup';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import imageToBase64 from 'image-to-base64/browser';
import { createSubCategory, editSubCategory } from '../../api/context/subcategory';
import { IoMdArrowBack } from 'react-icons/io';
import { FiEye, FiEyeOff, FiImage, FiPlus, FiTrash2 } from 'react-icons/fi';
import { Switch, Tooltip } from '@mui/material';

const CreateSubCategory = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = location;

    // Get data from navigation
    const subCategoryData = state?.subCategoryData;
    const categoryId = state?.categoryId;
    const categoryName = state?.categoryName;
    const isEditMode = !!subCategoryData;

    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef(null);

    const validationSchema = yup.object().shape({
        subCategoryId: yup
            .string()
            .trim()
            .required('Category is required'),
        title: yup
            .string()
            .trim()
            .required('Sub Category Name is required'),
        desc: yup
            .string()
            .trim()
            .required('Description is required'),
        img: isEditMode ? yup.string().nullable() : yup.string().required('Image is required'),
        isActive: yup
            .boolean()
            .required('isActive is required'),
        sizes: yup
            .array()
            .test('unique', 'Sizes must be unique', function (value) {
                if (!value) return true;
                return new Set(value.map(i => i.size?.toLowerCase())).size === value.length;
            }),
        sizeTypes: yup
            .array()
            .test('unique', 'Size types must be unique', function (value) {
                if (!value) return true;
                return new Set(value.map(i => i.sizeType?.toLowerCase())).size === value.length;
            }),
        parameters: yup
            .array()
            .test('unique', 'Parameters must be unique', function (value) {
                if (!value) return true;
                return new Set(value.map(i => i.parameter?.toLowerCase())).size === value.length;
            }),
    });

    const formik = useFormik({
        initialValues: {
            subCategoryId: categoryId || '',
            subSubCategoryId: '',
            title: '',
            desc: '',
            sizes: [],
            sizeTypes: [],
            parameters: [],
            tags: [],
            img: '',
            img_view: '',
            isActive: true
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            // Check for duplicates
            const hasDuplicateSizeTypes = new Set(
                values.sizeTypes.map(i => i.sizeType?.toLowerCase())
            ).size !== values.sizeTypes.length;

            const hasDuplicateSizes = new Set(
                values.sizes.map(i => i.size?.toLowerCase())
            ).size !== values.sizes.length;

            const hasDuplicateParameters = new Set(
                values.parameters.map(i => i.parameter?.toLowerCase())
            ).size !== values.parameters.length;

            if (hasDuplicateSizeTypes) {
                toast.error('Size types contain duplicate values!');
                return;
            }
            if (hasDuplicateSizes) {
                toast.error('Sizes contain duplicate values!');
                return;
            }
            if (hasDuplicateParameters) {
                toast.error('Parameters contain duplicate values!');
                return;
            }

            setLoading(true);

            // Prepare data for API
            const submitData = { ...values };
            if (submitData.img === '') {
                delete submitData.img;
            }
            delete submitData.img_view;

            if (isEditMode) {
                editSubCategory(submitData)((response) => {
                    if (response && response?.data?.statusCode === 200) {
                        toast.success("Sub Category Updated Successfully");
                        navigate(-1);
                    } else {
                        setLoading(false);
                        toast.error(response?.data?.message || "Update failed");
                    }
                });
            } else {
                createSubCategory(submitData)((response) => {
                    if (response && response?.data?.statusCode === 200) {
                        toast.success("Sub Category Created Successfully");
                        navigate(-1);
                    } else {
                        setLoading(false);
                        toast.error(response?.data?.message || "Creation failed");
                    }
                });
            }
        },
    });

    // Load data for edit mode
    useEffect(() => {
        if (isEditMode && subCategoryData) {
            formik.setValues({
                subCategoryId: subCategoryData.subcategoryId || categoryId || '',
                subSubCategoryId: subCategoryData.id || '',
                title: subCategoryData.title || '',
                desc: subCategoryData.description || '',
                sizes: subCategoryData.SubSubcategorySizes?.length > 0
                    ? subCategoryData.SubSubcategorySizes.map((i, idx) => ({
                        size: i.size,
                        sizeIndex: i.sizeIndex ?? (idx + 1)
                    }))
                    : [],
                tags: subCategoryData.tags?.map(tag => ({
                    id: tag.id,
                    tagName: tag.tagName || '',
                    tagImage: tag.tagImageUrl,
                    tagImagePreview: tag.tagImageUrl,
                    isActive: tag.isActive ?? true,
                    isVisibleOnApp: tag.isVisibleOnApp ?? true
                })) || [],
                sizeTypes: subCategoryData.SubSubCategorySizeTypes?.length > 0
                    ? subCategoryData.SubSubCategorySizeTypes.map(i => ({ sizeType: i.sizeType }))
                    : [],
                parameters: subCategoryData.SubSubcategoryParameters?.length > 0
                    ? subCategoryData.SubSubcategoryParameters.map(i => ({ parameter: i.parameter }))
                    : [],
                img: '',
                img_view: subCategoryData.img || '',
                isActive: subCategoryData.isActive ?? true
            });
        } else if (!isEditMode && categoryId) {
            // For create mode, pre-fill the categoryId
            formik.setFieldValue('subCategoryId', categoryId);
        }
    }, [isEditMode, subCategoryData, categoryId]);

    const handleImageChange = async (e) => {
        const file = e?.target?.files[0];
        if (!file) return;

        const maxSizeInBytes = 2 * 1024 * 1024;
        const validImageTypes = ['image/jpeg', 'image/png', 'image/gif'];

        if (file?.size > maxSizeInBytes) {
            toast.error("File size exceeds 2 MB limit");
            return;
        }

        try {
            const response = await imageToBase64(URL.createObjectURL(file));
            formik.setValues({
                ...formik.values,
                img: response,
                img_view: 'data:image/jpeg;base64,' + response,
            });
        } catch (error) {
            toast.error("Failed to process image");
        }
    };

    const handleImageContainerClick = () => {
        fileInputRef.current?.click();
    };

    const handleProductAddSize = () => {
        const updatedProductSizes = [...formik.values.sizes];
        const nextIndex = updatedProductSizes.length > 0
            ? Math.max(...updatedProductSizes.map(s => s.sizeIndex || 0)) + 1
            : 1;

        updatedProductSizes.push({
            size: '',
            sizeIndex: nextIndex
        });
        formik.setFieldValue('sizes', updatedProductSizes);
    };

    const handleSpecificationAdd = () => {
        const updatedProductSpecifications = [...formik.values.parameters];
        updatedProductSpecifications.push({ parameter: '' });
        formik.setFieldValue('parameters', updatedProductSpecifications);
    };

    const handleProductSizeStockChange = (index, event) => {
        const updatedSizes = [...formik.values.sizes];
        const fieldName = event.target.name;
        let fieldValue = event.target.value;

        if (fieldName === 'size') {
            fieldValue = fieldValue.toUpperCase();
        }

        updatedSizes[index][fieldName] = fieldValue;
        formik.setFieldValue('sizes', updatedSizes);
    };

    const handleProductSpecificationChange = (index, event) => {
        const updatedSpecifications = [...formik.values.parameters];
        updatedSpecifications[index][event.target.name] = event.target.value;
        formik.setFieldValue('parameters', updatedSpecifications);
    };

    const handleDeleteProductSizeStock = (index) => {
        const updatedSizes = formik.values.sizes.filter((_, i) => i !== index);
        formik.setFieldValue('sizes', updatedSizes);
    };

    const handleDeleteProductSpecification = (index) => {
        const updatedSpecifications = formik.values.parameters.filter((_, i) => i !== index);
        formik.setFieldValue('parameters', updatedSpecifications);
    };

    const handleProductAddSizeType = () => {
        const updatedProductSizeTypes = [...formik.values.sizeTypes];
        updatedProductSizeTypes.push({ sizeType: '' });
        formik.setFieldValue('sizeTypes', updatedProductSizeTypes);
    };

    const handleProductSizeTypeChange = (index, event) => {
        const updatedSizeTypes = [...formik.values.sizeTypes];
        updatedSizeTypes[index][event.target.name] = event.target.value;
        formik.setFieldValue('sizeTypes', updatedSizeTypes);
    };

    const handleDeleteProductSizeType = (index) => {
        const updatedSizeTypes = formik.values.sizeTypes.filter((_, i) => i !== index);
        formik.setFieldValue('sizeTypes', updatedSizeTypes);
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(formik.values.sizes);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        const updatedItems = items.map((item, index) => ({
            ...item,
            sizeIndex: index + 1
        }));

        formik.setFieldValue('sizes', updatedItems);
    };

    const onDragEndSizeTypes = (result) => {
        if (!result.destination) return;

        const items = Array.from(formik.values.sizeTypes);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        formik.setFieldValue('sizeTypes', items);
    };

    // Add new tag
    const handleTagAdd = () => {
        const newTag = {
            tagName: '',
            tagImage: '',
            tagImagePreview: '',
            isActive: true,
            isVisibleOnApp: true
        };

        formik.setFieldValue('tags', [
            newTag,
            ...formik.values.tags
        ]);
    };

    // Handle tag field changes
    const handleTagChange = (index, e) => {
        const { name, value } = e.target;
        const updatedTags = formik.values.tags.map((tag, i) =>
            i === index ? { ...tag, [name]: value } : tag
        );
        formik.setFieldValue('tags', updatedTags);
    };

    // Handle tag image upload
    const handleTagImageChange = (index, e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const updatedTags = formik.values.tags.map((tag, i) =>
                    i === index ? {
                        ...tag,
                        tagImage: e.target.result,
                        tagImagePreview: e.target.result
                    } : tag
                );
                formik.setFieldValue('tags', updatedTags);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle tag status change
    const handleTagStatusChange = (index, checked) => {
        const updatedTags = formik.values.tags.map((tag, i) =>
            i === index ? { ...tag, isActive: checked } : tag
        );
        formik.setFieldValue('tags', updatedTags);
    };

    // Handle tag visibility change
    const handleTagVisibilityChange = (index, isVisibleOnApp) => {
        const updatedTags = formik.values.tags.map((tag, i) =>
            i === index ? { ...tag, isVisibleOnApp } : tag
        );
        formik.setFieldValue('tags', updatedTags);
    };

    // Delete tag
    const handleDeleteTag = (index) => {
        const updatedTags = formik.values.tags.filter((_, i) => i !== index);
        formik.setFieldValue('tags', updatedTags);
    };

    return (
        <Container fluid className="py-4">
            <div className="col-12">
                <div className="page-title-box d-flex align-items-center mb-4">
                    <div
                        onClick={() => navigate(-1)}
                        style={{ cursor: 'pointer', fontSize: '24px', fontWeight: '800', display: 'flex', alignItems: 'center' }}
                    >
                        <IoMdArrowBack className='icon' />
                    </div>
                    <h4 className="mb-0 fw-bolder">{isEditMode ? 'Edit' : 'Create'} Sub Category {categoryName === undefined ? '' : '(' + categoryName + ')'}</h4>
                </div>
            </div>

            {/* Sub Category Information Card */}
            <Card className="mb-4">
                <Card.Body className="p-4">
                    <Form onSubmit={formik.handleSubmit}>
                        <Row>
                            {/* Left Column - Name and Description */}
                            <Col md={8}>
                                <Row>
                                    <Col md={12}>
                                        <Form.Group className="mb-3" controlId="formBasicEmail">
                                            <Form.Label style={{
                                                fontSize: '14px',
                                                fontWeight: '600',
                                            }}>Sub Category Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter Sub Category Name"
                                                name='title'
                                                value={formik.values?.title}
                                                onChange={formik.handleChange}
                                                isValid={formik.touched.title && !formik.errors.title}
                                                isInvalid={formik.touched.title && !!formik.errors.title}
                                                className="rounded-input"
                                                style={{
                                                    height: '40px', // Reduced height
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Control.Feedback type="invalid">{formik.errors.title}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={12}>
                                        <Form.Group className="mb-0">
                                            <Form.Label style={{
                                                fontSize: '14px',
                                                fontWeight: '600',
                                            }}>Sub Category Description</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3} // Reduced rows
                                                placeholder='Enter description here...'
                                                name='desc'
                                                value={formik.values?.desc}
                                                onChange={formik.handleChange}
                                                isValid={formik.touched.desc && !formik.errors.desc}
                                                isInvalid={formik.touched.desc && !!formik.errors.desc}
                                                style={{
                                                    borderRadius: '8px',
                                                    minHeight: '80px', // Reduced height
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Control.Feedback type="invalid">{formik.errors.desc}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Col>

                            {/* Right Column - Image */}
                            <Col md={4}>
                                <Form.Group className="mb-0">
                                    <Form.Label style={{
                                        fontSize: '14px',
                                        fontWeight: '600',
                                    }}>Sub Category Image</Form.Label>
                                    <div
                                        className="image-upload-container"
                                        onClick={handleImageContainerClick}
                                        style={{
                                            border: '2px dashed #ddd',
                                            borderRadius: '8px',
                                            padding: '15px',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            minHeight: '180px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: formik.values.img_view ? 'transparent' : '#f8f9fa'
                                        }}
                                    >
                                        {formik.values.img_view ? (
                                            <>
                                                <img
                                                    src={formik.values.img_view}
                                                    alt='Category'
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: '130px', // Reduced height
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                                <div className="image-overlay">
                                                    <BiImageAdd size={20} /> {/* Reduced size */}
                                                    <span>Change Image</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="">
                                                <BiImageAdd size={24} className="text-muted mb-1" /> {/* Reduced size */}
                                                <p className="text-muted mb-0" style={{ fontSize: '12px' }}>Click to upload image</p>
                                            </div>
                                        )}
                                        <Form.Control
                                            ref={fileInputRef}
                                            type="file"
                                            onChange={handleImageChange}
                                            isInvalid={formik.touched.img && formik.errors.img}
                                            error={formik.errors.img}
                                            style={{ display: 'none' }}
                                            accept="image/*"
                                        />
                                    </div>
                                    <Form.Control.Feedback type="invalid">{formik.errors.img}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* Sizes Card */}
            <Card className="mb-4">
                <Card.Header className="bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                        <Row className=" align-items-center">
                            <span
                                style={{
                                    fontSize: "16px",
                                    fontWeight: "700",
                                }}
                            >
                                Sizes
                            </span> <span
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "400",
                                }}
                            >
                                Drag sizes to reorder according to the size preference *
                            </span>
                        </Row>

                        <Button
                            variant="outline-primary"
                            onClick={handleProductAddSize}
                            className="subcategory-add-button"
                        >
                            <FiPlus /> Add new size
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body className="px-4 py-5">
                    {formik.values.sizes.length > 0 ? (
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="sizes" direction="horizontal">
                                {(provided) => (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            gap: "12px",
                                            overflowX: "auto",
                                            paddingBottom: "8px",
                                            scrollbarWidth: "none",
                                            msOverflowStyle: "none",
                                        }}
                                        className="hide-scrollbar"
                                    >
                                        {formik.values.sizes.map((item, index) => (
                                            <Draggable
                                                key={index}
                                                draggableId={`size-${index}`}
                                                index={index}
                                            >
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "8px",
                                                            flex: "0 0 auto",
                                                            padding: "8px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#ffffff",
                                                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                                            ...provided.draggableProps.style,
                                                        }}
                                                    >
                                                        <Form.Group className="mb-0 me-0">
                                                            <Form.Control
                                                                type="text"
                                                                placeholder="e.g., L, M, S"
                                                                name="size"
                                                                value={item.size}
                                                                onChange={(e) =>
                                                                    handleProductSizeStockChange(index, e)
                                                                }
                                                                required
                                                                style={{
                                                                    width: "120px",
                                                                    borderRadius: "6px",
                                                                    border: "0",
                                                                    backgroundColor: "#f9f9f9",
                                                                }}
                                                            />
                                                        </Form.Group>

                                                        <FiTrash2
                                                            role="button"
                                                            onClick={() => handleDeleteProductSizeStock(index)}
                                                            style={{
                                                                fontSize: "20px",
                                                                cursor: "pointer",
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    ) : (
                        <p className="text-muted text-center my-5">No sizes added yet</p>
                    )}
                </Card.Body>
            </Card>

            {/* Size Types Card */}
            <Card className="mb-4">
                <Card.Header className="bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                        <span style={{
                            fontSize: '16px',
                            fontWeight: '700',
                        }}>
                            Size types
                        </span>
                        <Button
                            variant="outline-primary"
                            onClick={handleProductAddSizeType}
                            className="subcategory-add-button"
                        >
                            <FiPlus />  Add size type
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body className="px-4 py-5">
                    {formik.values.sizeTypes.length > 0 ? (
                        <DragDropContext onDragEnd={onDragEndSizeTypes}>
                            <Droppable
                                droppableId="sizeTypes"
                                direction="horizontal"
                            >
                                {(provided) => (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className="sizes-grid"
                                    >
                                        {formik.values.sizeTypes.map((item, index) => (
                                            <Draggable
                                                key={index}
                                                draggableId={`sizeType-${index}`}
                                                index={index}
                                            >
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className="size-item"
                                                    >
                                                        <Form.Group className="mb-0 flex-grow-1 me-2">
                                                            <Form.Control
                                                                type="text"
                                                                placeholder="e.g., Waist, Length"
                                                                name="sizeType"
                                                                value={item.sizeType}
                                                                onChange={(e) =>
                                                                    handleProductSizeTypeChange(index, e)
                                                                }
                                                                required
                                                                style={{ borderRadius: "6px", border: "0", backgroundColor: "#ffffff" }}
                                                            />
                                                        </Form.Group>

                                                        <FiTrash2
                                                            role="button"
                                                            onClick={() =>
                                                                handleDeleteProductSizeType(index)
                                                            }
                                                            style={{
                                                                fontSize: "20px",
                                                                cursor: "pointer",
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    ) : (
                        <p className="text-muted text-center  my-5">No size types added yet</p>
                    )}
                </Card.Body>
            </Card>

            {/* Specifications Card */}
            <Card className="mb-4">
                <Card.Header className="bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                        <span style={{
                            fontSize: '16px',
                            fontWeight: '700',
                        }}>
                            Specification
                        </span>
                        <Button
                            variant="outline-primary"
                            onClick={handleSpecificationAdd}
                            className="subcategory-add-button"
                        >
                            <FiPlus />  Add specification
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body className="px-4 py-5">
                    {formik.values.parameters.length > 0 ? (
                        <div className='size_grid_wrapper'>
                            {formik.values.parameters.map((item, index) => (
                                <div key={index} className='d-flex align-items-center mb-2 p-2 border rounded'>
                                    <Form.Group className="me-3 flex-grow-1">
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter specification"
                                            name='parameter'
                                            value={item.parameter}
                                            onChange={(e) => handleProductSpecificationChange(index, e)}
                                            required
                                            style={{ borderRadius: '6px' }}
                                        />
                                    </Form.Group>

                                    <FiTrash2
                                        role='button'
                                        onClick={() => handleDeleteProductSpecification(index)}
                                        style={{ fontSize: "25px", color: "red" }}
                                        className='delete_size_btn'
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-muted text-center my-5">No specifications added yet</p>
                    )}
                </Card.Body>
            </Card>

            {/* Tags Card - Table Layout */}
            <Card className="mb-4">
                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                    <span style={{ fontSize: '16px', fontWeight: '700' }}>
                        Tags
                    </span>
                    <Button
                        onClick={handleTagAdd}
                        variant="outline-dark"
                        className="subcategory-add-button fs-8"
                    >
                        <FiPlus /> Add New Tag
                    </Button>
                </Card.Header>

                <Card.Body className="p-4">
                    {formik.values.tags.length !== 0 && (
                        <div className="table-responsive bg-light rounded">
                            <table className="table mb-0">
                                <thead>
                                    <tr>
                                        <th className="fw-semibold text-center py-3">Image</th>
                                        <th className="fw-semibold text-center py-3">Tag Name</th>
                                        <th className="fw-semibold text-center py-3">Status</th>
                                        <th className="fw-semibold text-center py-3">Toggle</th>
                                        <th className="fw-semibold text-center py-3">App Visibility</th>
                                        <th className="fw-semibold text-center py-3">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formik.values.tags.map((tag, index) => (
                                        <tr key={index} className="border-bottom">
                                            {/* Image Column */}
                                            <td className="text-center align-middle">
                                                <div className="d-flex justify-content-center ">
                                                    <div
                                                        className="position-relative border rounded"
                                                        style={{
                                                            width: '60px',
                                                            height: '60px',
                                                            cursor: 'pointer',
                                                            overflow: 'hidden',
                                                            backgroundColor: '#ffffffff'
                                                        }}
                                                    >
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(e) => handleTagImageChange(index, e)}
                                                            className="position-absolute top-0 start-0 w-100 h-100"
                                                            style={{
                                                                opacity: 0,
                                                                cursor: 'pointer',
                                                                zIndex: 2,
                                                                backgroundColor: '#ffffffff'
                                                            }}
                                                        />

                                                        {tag.tagImagePreview ? (
                                                            <img
                                                                src={tag.tagImagePreview}
                                                                alt="Tag"
                                                                className="position-absolute top-0 start-0 w-100 h-100"
                                                                style={{
                                                                    objectFit: 'cover',
                                                                    zIndex: 1,
                                                                    backgroundColor: '#ffffffff'
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-white">
                                                                <FiImage className="text-muted" size={20} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Tag Name Column */}
                                            <td className="text-center align-middle">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter tag name"
                                                    name='tagName'
                                                    value={tag.tagName}
                                                    onChange={(e) => handleTagChange(index, e)}
                                                    className="rounded-input form-control-sm mx-auto"
                                                />
                                            </td>

                                            {/* Status Column */}
                                            <td className="text-center align-middle">
                                                {tag.isActive ? (
                                                    <span className="badge bg-success" style={{ fontSize: '0.8rem', padding: '0.5em 1em' }}>Active</span>
                                                ) : (
                                                    <span className="badge bg-danger" style={{ fontSize: '0.8rem', padding: '0.5em 1em' }}>Inactive</span>
                                                )}
                                            </td>

                                            {/* Toggle Column */}
                                            <td className="text-center align-middle">
                                                <div className="d-flex justify-content-center">
                                                    <Switch
                                                        size="20px"
                                                        id={`tag-switch-${index}`}
                                                        checked={tag.isActive}
                                                        onChange={(e) => handleTagStatusChange(index, e.target.checked)}
                                                        className="custom-switch"
                                                    />
                                                </div>
                                            </td>

                                            {/* App Visibility Column */}
                                            <td className="text-center align-middle">
                                                <div className="d-flex justify-content-center">
                                                    <Tooltip title={tag.isVisibleOnApp ? "Visible in app" : "Hidden in app"} disableInteractive placement="top">
                                                        <div
                                                            className="visibility_icon_bg"
                                                            onClick={() => handleTagVisibilityChange(index, !tag.isVisibleOnApp)}
                                                            style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                borderRadius: '50%',
                                                                cursor: 'pointer',
                                                                backgroundColor: tag.isVisibleOnApp ? '#e8f5e8' : '#f2f2f2',
                                                                transition: 'background-color 0.3s ease'
                                                            }}
                                                        >
                                                            {tag.isVisibleOnApp ? (
                                                                <FiEye className="text-success fs-5" />
                                                            ) : (
                                                                <FiEyeOff className="text-muted fs-5" />
                                                            )}
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                            </td>

                                            {/* Action Column */}
                                            <td className="text-center align-middle">
                                                <div className="d-flex justify-content-center">
                                                    <Tooltip title="Delete" disableInteractive placement="top">
                                                        <div
                                                            className="delete_icon_bg"
                                                            onClick={() => handleDeleteTag(index)}
                                                            style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                borderRadius: '50%',
                                                                cursor: 'pointer',
                                                                backgroundColor: '#f2f2f2ff'
                                                            }}
                                                        >
                                                            <FiTrash2 className="delete_icon text-black fs-5" />
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {formik.values.tags.length === 0 && (
                        <div className="text-center  py-5">
                            <p className="text-muted  py-5">No tags added yet</p>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {checkUserPermissions('manage_sub_category') && (
                <div className="fixed-bottom-right">
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={loading}
                        size="lg"
                        className="request_btn"
                        onClick={formik.handleSubmit}
                    >
                        {loading ? " Processing subcategory .. " : (isEditMode ? "Update Sub Category" : "Create Sub Category ") }
                    </Button>
                </div>
            )}

            <style jsx>{`
                .image-upload-container {
                    transition: all 0.3s ease;
                }
                .image-upload-container:hover {
                    border-color: #779be1 !important;
                    background-color: #f8f9fa;
                }
                .image-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.7);
                    color: white;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    opacity: 0;
                    transition: opacity 0.3s ease;
                    border-radius: 6px;
                }
                .image-upload-container:hover .image-overlay {
                    opacity: 1;
                }
                .image-placeholder {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                }
                .rounded-input {
                    border-radius: 8px !important;
                }
                .drag-item {
                    transition: transform 0.2s ease;
                }
                .drag-item:hover {
                    background-color: #f8f9fa;
                }
                .sizes-grid {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 10px;
                }
                .size-item {
                    display: flex;
                    align-items: center;
                    background-color: #f8f9fa;
                    padding: 10px;
                    border-radius: 6px;
                    min-width: 150px;
                }
            `}</style>
        </Container>
    );
};

export default CreateSubCategory;